import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  Building2,
  CreditCard,
  Shield,
  Tag,
  Settings,
  Eye,
  Clock,
  Activity,
  Users,
  Briefcase,
  Headphones,
  Plug
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors
const categoryConfig = {
  user_management: { icon: Users, color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'User Management' },
  company_management: { icon: Building2, color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', label: 'Company Management' },
  msp_management: { icon: Briefcase, color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30', label: 'MSP Management' },
  billing: { icon: CreditCard, color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Billing' },
  promotion: { icon: Tag, color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'Promotions' },
  support: { icon: Headphones, color: 'bg-pink-500/20 text-pink-400 border-pink-500/30', label: 'Support' },
  system: { icon: Settings, color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', label: 'System' },
  security: { icon: Shield, color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Security' },
  integration: { icon: Plug, color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30', label: 'Integrations' },
  settings: { icon: Settings, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Settings' },
};

// Role badges
const roleBadges = {
  super_admin: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'Super Admin' },
  support: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Support' },
  billing_admin: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Billing Admin' },
};

export default function SystemAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    actor_role: '',
    category: '',
    search: '',
    start_date: '',
    end_date: ''
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 25,
    total: 0,
    has_more: false
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [actionTypes, setActionTypes] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filters.actor_role) params.append('actor_role', filters.actor_role);
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('skip', pagination.skip.toString());
      params.append('limit', pagination.limit.toString());
      
      const response = await axios.get(`${API}/api/admin/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        has_more: response.data.has_more
      }));
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.skip, pagination.limit]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/audit-logs/stats?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchActionTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/admin/audit-logs/actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActionTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch action types:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchActionTypes();
  }, [fetchLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, skip: 0 })); // Reset to first page
  };

  const handleNextPage = () => {
    if (pagination.has_more) {
      setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }));
    }
  };

  const clearFilters = () => {
    setFilters({
      actor_role: '',
      category: '',
      search: '',
      start_date: '',
      end_date: ''
    });
    setPagination(prev => ({ ...prev, skip: 0 }));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return format(new Date(timestamp), 'MMM d, yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  const getCategoryBadge = (category) => {
    const config = categoryConfig[category] || categoryConfig.system;
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getRoleBadge = (role) => {
    const config = roleBadges[role] || roleBadges.support;
    return (
      <Badge variant="outline" className={config.color}>
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">System Audit Logs</h1>
          <p className="text-gray-400 mt-1">Track all administrative actions across the platform</p>
        </div>
        <Button 
          onClick={() => { fetchLogs(); fetchStats(); }}
          variant="outline"
          className="border-gray-700"
          disabled={loading}
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {/* Loading State */}
      {loading && !logs.length && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-gray-400">Loading audit logs...</p>
          </div>
        </div>
      )}

      {/* Content - only show when not in initial loading state */}
      {(!loading || logs.length > 0) && (
        <>
          {/* Stats Cards */}
          {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#1a1a2e] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Actions (30d)</p>
                  <p className="text-2xl font-bold text-white">{stats.total_actions}</p>
                </div>
                <Activity className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a2e] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Active Admins</p>
                  <p className="text-2xl font-bold text-white">{stats.by_actor?.length || 0}</p>
                </div>
                <Users className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a2e] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">User Actions</p>
                  <p className="text-2xl font-bold text-white">{stats.by_category?.user_management || 0}</p>
                </div>
                <User className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#1a1a2e] border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Company Actions</p>
                  <p className="text-2xl font-bold text-white">{stats.by_category?.company_management || 0}</p>
                </div>
                <Building2 className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-[#1a1a2e] border-gray-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 bg-[#0f0f1a] border-gray-700 text-white"
              />
            </div>
            
            <Select value={filters.actor_role || 'all'} onValueChange={(v) => handleFilterChange('actor_role', v === 'all' ? '' : v)}>
              <SelectTrigger className="bg-[#0f0f1a] border-gray-700 text-white">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-gray-700">
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="billing_admin">Billing Admin</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filters.category || 'all'} onValueChange={(v) => handleFilterChange('category', v === 'all' ? '' : v)}>
              <SelectTrigger className="bg-[#0f0f1a] border-gray-700 text-white">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a2e] border-gray-700">
                <SelectItem value="all">All Categories</SelectItem>
                {actionTypes?.categories && Object.entries(actionTypes.categories).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="bg-[#0f0f1a] border-gray-700 text-white"
              placeholder="Start Date"
            />
            
            <div className="flex gap-2">
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                className="bg-[#0f0f1a] border-gray-700 text-white flex-1"
                placeholder="End Date"
              />
              <Button 
                variant="ghost" 
                onClick={clearFilters}
                className="text-gray-400 hover:text-white"
              >
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="bg-[#1a1a2e] border-gray-800">
        <CardHeader>
          <CardTitle className="text-white">Audit Trail</CardTitle>
          <CardDescription>
            Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total} entries
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">No audit logs found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800">
                    <TableHead className="text-gray-400">Timestamp</TableHead>
                    <TableHead className="text-gray-400">Admin</TableHead>
                    <TableHead className="text-gray-400">Role</TableHead>
                    <TableHead className="text-gray-400">Category</TableHead>
                    <TableHead className="text-gray-400">Action</TableHead>
                    <TableHead className="text-gray-400">Target</TableHead>
                    <TableHead className="text-gray-400 text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="border-gray-800 hover:bg-[#0f0f1a]">
                      <TableCell className="text-gray-300">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell className="text-white">
                        {log.actor?.email || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {getRoleBadge(log.actor?.role)}
                      </TableCell>
                      <TableCell>
                        {getCategoryBadge(log.category)}
                      </TableCell>
                      <TableCell className="text-gray-300 max-w-xs truncate">
                        {log.description}
                      </TableCell>
                      <TableCell className="text-gray-400">
                        {log.target?.name || log.target?.id?.substring(0, 8) || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedLog(log)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-gray-400 text-sm">
                  Page {Math.floor(pagination.skip / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={pagination.skip === 0}
                    className="border-gray-700"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!pagination.has_more}
                    className="border-gray-700"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
        </>
      )}

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Audit Log Details</DialogTitle>
            <DialogDescription>
              {formatTimestamp(selectedLog?.timestamp)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-4">
              {/* Actor Info */}
              <div className="p-4 bg-[#0f0f1a] rounded-lg">
                <h4 className="text-gray-400 text-sm mb-2">Performed By</h4>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{selectedLog.actor?.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getRoleBadge(selectedLog.actor?.role)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Info */}
              <div className="p-4 bg-[#0f0f1a] rounded-lg">
                <h4 className="text-gray-400 text-sm mb-2">Action</h4>
                <div className="flex items-center gap-2 mb-2">
                  {getCategoryBadge(selectedLog.category)}
                  <Badge variant="outline" className="border-gray-600 text-gray-300">
                    {selectedLog.action}
                  </Badge>
                </div>
                <p className="text-white">{selectedLog.description}</p>
              </div>
              
              {/* Target Info */}
              {selectedLog.target && (
                <div className="p-4 bg-[#0f0f1a] rounded-lg">
                  <h4 className="text-gray-400 text-sm mb-2">Target</h4>
                  <div className="space-y-1">
                    <p className="text-white">
                      <span className="text-gray-400">Type:</span> {selectedLog.target.type}
                    </p>
                    {selectedLog.target.name && (
                      <p className="text-white">
                        <span className="text-gray-400">Name:</span> {selectedLog.target.name}
                      </p>
                    )}
                    {selectedLog.target.id && (
                      <p className="text-white">
                        <span className="text-gray-400">ID:</span> {selectedLog.target.id}
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Additional Details */}
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div className="p-4 bg-[#0f0f1a] rounded-lg">
                  <h4 className="text-gray-400 text-sm mb-2">Additional Details</h4>
                  <pre className="text-gray-300 text-sm overflow-auto max-h-40">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Metadata */}
              {selectedLog.metadata && (
                <div className="p-4 bg-[#0f0f1a] rounded-lg">
                  <h4 className="text-gray-400 text-sm mb-2">Request Metadata</h4>
                  <div className="space-y-1 text-sm">
                    {selectedLog.metadata.ip_address && (
                      <p className="text-gray-300">
                        <span className="text-gray-500">IP:</span> {selectedLog.metadata.ip_address}
                      </p>
                    )}
                    {selectedLog.metadata.user_agent && (
                      <p className="text-gray-300 truncate">
                        <span className="text-gray-500">User Agent:</span> {selectedLog.metadata.user_agent}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
